import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { useBugEvaluations } from '../../../hooks/useBugEvaluation.js';
import type { BugEvaluationListItem } from '../../../lib/bug-evaluation-api.js';
import { compareTableValues } from '../../../lib/table-sort.js';
import { SortableHeader, type SortDir } from '../KpiDetailModal/SortableHeader.js';
import type { IpointTrendDateBasis, KpiTeam } from '../../../types/api.js';

interface IpointMonthDetailModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 対象月（YYYY-MM 形式） */
  month: string;
  /** 対象チーム（未指定時は全体） */
  team?: KpiTeam;
  /** チャートの集計基準日（起因案件リリース日 / バグ起票日）。明細も同じ軸で絞り込む */
  dateBasis: IpointTrendDateBasis;
  /** 表示用チーム名 */
  teamDisplayName: string;
  /** チャートで表示している合計i-point（説明文用） */
  totalIpoint: number;
  /** チャートで表示しているバグ件数（説明文用） */
  bugCount: number;
}

/**
 * 並び替え可能な列。値はそのまま BugEvaluationListItem のフィールド名
 * @graph-connects none
 */
type IpointMonthSortKey = keyof Pick<
  BugEvaluationListItem,
  | 'issueKey'
  | 'summary'
  | 'iPoint'
  | 'causeProjectKey'
  | 'causeProjectSummary'
  | 'causeProjectDevLeader'
  | 'devLeaderGroup'
  | 'devLeaderTeam'
  | 'bugType'
  | 'negligenceLevel'
>;

/**
 * 並び替え用の値を取り出す。空文字は「未設定」として null に寄せ、昇順・降順どちらでも末尾に並べる
 * @graph-connects none
 */
function getSortValue(bug: BugEvaluationListItem, key: IpointMonthSortKey): string | number | null {
  const value = bug[key];
  return value === '' ? null : value;
}

/**
 * 月文字列（YYYY-MM）から月の開始日と終了日を算出
 * @graph-connects none
 */
function getMonthRange(month: string): { startDate: string; endDate: string } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return null;
  const year = Number(match[1]);
  const monthNum = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(monthNum)) return null;
  const lastDay = new Date(year, monthNum, 0).getDate();
  const mm = String(monthNum).padStart(2, '0');
  const dd = String(lastDay).padStart(2, '0');
  return {
    startDate: `${String(year)}-${mm}-01`,
    endDate: `${String(year)}-${mm}-${dd}`,
  };
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 月別i-point内訳ドリルダウンモーダル。クリックされた月のバグ評価一覧（チャートと同じ起因案件リリース日 / バグ起票日の軸）を表示し、起因案件の実装担当者・iPoint・チームを確認できる。各列ヘッダーのクリックで昇順/降順に並び替えられる（初期は i-point 降順）
 * @graph-connects useBugEvaluations [calls] 指定月のバグ評価一覧を取得
 * @graph-connects Modal [calls] 汎用モーダルで内容を表示
 * @graph-connects SortableHeader [calls] 列ヘッダーのソートUI
 * @graph-connects compareTableValues [calls] 列値の比較（未設定は末尾）
 */
export function IpointMonthDetailModal({
  open,
  onClose,
  month,
  team,
  dateBasis,
  teamDisplayName,
  totalIpoint,
  bugCount,
}: IpointMonthDetailModalProps) {
  const { t } = useTranslation();

  const range = useMemo(() => getMonthRange(month), [month]);

  const params = useMemo(() => {
    if (!open || !range) return undefined;
    return {
      startDate: range.startDate,
      endDate: range.endDate,
      team,
      // i-point月次推移チャート（起因案件リリース日 / バグ起票日ベース集計）と同じ軸で明細を絞り込む。
      // 既定の評価日ベースだと、ヘッダーの件数（チャートの集計軸）と食い違う。
      dateBasis,
    };
  }, [open, range, team, dateBasis]);

  const { data, isLoading } = useBugEvaluations(params);

  // 初期表示は i-point の大きい順（過失度の高いバグから確認できるように）
  const [sortKey, setSortKey] = useState<IpointMonthSortKey>('iPoint');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: IpointMonthSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // 数値列は大きい順、文字列列は昇順から始める
      setSortDir(key === 'iPoint' ? 'desc' : 'asc');
    }
  };

  const sortedBugs = useMemo(() => {
    if (!data?.data) return [];
    return [...data.data].sort((a, b) =>
      compareTableValues(getSortValue(a, sortKey), getSortValue(b, sortKey), sortDir),
    );
  }, [data, sortKey, sortDir]);

  const renderSortableHeader = (key: IpointMonthSortKey, label: string) => (
    <SortableHeader
      label={label}
      sortKey={key}
      currentKey={sortKey}
      currentDir={sortDir}
      onSort={handleSort}
    />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={t('kpi.ipointMonth.title', { month })}
      description={t(
        dateBasis === 'createdAt'
          ? 'kpi.ipointMonth.descriptionCreatedAt'
          : 'kpi.ipointMonth.description',
        {
          team: teamDisplayName,
          month,
          totalIpoint,
          bugCount,
        },
      )}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner" />
        </div>
      ) : sortedBugs.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]">
          {t('kpi.ipointMonth.noItems')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1300px]">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[140px]">
                  {renderSortableHeader('issueKey', t('kpi.ipointMonth.issueKey'))}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] min-w-[200px]">
                  {renderSortableHeader('summary', t('kpi.ipointMonth.summary'))}
                </th>
                <th className="text-right py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[80px]">
                  {renderSortableHeader('iPoint', t('kpi.ipointMonth.iPoint'))}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[140px]">
                  {renderSortableHeader('causeProjectKey', t('kpi.ipointMonth.causeProjectKey'))}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] min-w-[200px]">
                  {renderSortableHeader(
                    'causeProjectSummary',
                    t('kpi.ipointMonth.causeProjectSummary'),
                  )}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[140px]">
                  {renderSortableHeader(
                    'causeProjectDevLeader',
                    t('kpi.ipointMonth.causeProjectDevLeader'),
                  )}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[80px]">
                  {renderSortableHeader('devLeaderGroup', t('kpi.ipointMonth.group'))}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[140px]">
                  {renderSortableHeader('devLeaderTeam', t('kpi.ipointMonth.team'))}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[100px]">
                  {renderSortableHeader('bugType', t('kpi.ipointMonth.bugType'))}
                </th>
                <th className="text-left py-2 px-2 font-medium text-[var(--color-text-muted)] whitespace-nowrap w-[110px]">
                  {renderSortableHeader('negligenceLevel', t('kpi.ipointMonth.negligenceLevel'))}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedBugs.map((bug, index) => (
                <tr
                  key={`${bug.issueKey}-${String(index)}`}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="py-2 px-2 whitespace-nowrap">
                    <a
                      href={`https://air-closet.backlog.jp/view/${bug.issueKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-accent-primary)] hover:underline"
                    >
                      {bug.issueKey}
                    </a>
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-primary)]">
                    <div className="line-clamp-2">{bug.summary || '-'}</div>
                  </td>
                  <td className="py-2 px-2 text-right text-[var(--color-text-primary)] whitespace-nowrap font-medium">
                    {bug.iPoint}
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    {bug.causeProjectKey ? (
                      <a
                        href={`https://air-closet.backlog.jp/view/${bug.causeProjectKey}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--color-accent-primary)] hover:underline"
                      >
                        {bug.causeProjectKey}
                      </a>
                    ) : (
                      <span className="text-[var(--color-text-muted)]">-</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-primary)]">
                    <div className="line-clamp-2">
                      {bug.causeProjectSummary || (
                        <span className="text-[var(--color-text-muted)]">-</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-primary)] whitespace-nowrap">
                    {bug.causeProjectDevLeader || (
                      <span className="text-[var(--color-text-muted)]">-</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.devLeaderGroup || (
                      <span className="text-[var(--color-text-muted)]">-</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.devLeaderTeam || <span className="text-[var(--color-text-muted)]">-</span>}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.bugType || '-'}
                  </td>
                  <td className="py-2 px-2 text-[var(--color-text-secondary)] whitespace-nowrap">
                    {bug.negligenceLevel || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[var(--color-border)]">
                <td
                  colSpan={2}
                  className="py-2 px-2 text-right font-medium text-[var(--color-text-muted)]"
                >
                  {t('kpi.ipointMonth.totalLabel')}
                </td>
                <td className="py-2 px-2 text-right font-semibold text-[var(--color-text-primary)] whitespace-nowrap">
                  {sortedBugs.reduce((sum, b) => sum + b.iPoint, 0)}
                </td>
                <td colSpan={7} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Modal>
  );
}
